import React from 'react';
import { Save, Link2, Unlink, Loader } from 'lucide-react';
import { metaAdsService } from '../../../backend/services/metaAds';

type Mapping = { id: string; external_campaign_id: string; budget_id: string | null };

export const BudgetMetaMappingPanel: React.FC<{
  budgetId: string;
  existing?: Mapping | null;
  onSaved?: (mapping: Mapping | null) => void;
}> = ({ budgetId, existing, onSaved }) => {
  const [externalId, setExternalId] = React.useState(existing?.external_campaign_id || '');
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState<'idle' | 'valid' | 'invalid'>('idle');

  React.useEffect(() => {
    setExternalId(existing?.external_campaign_id || '');
  }, [existing?.external_campaign_id]);

  const save = async () => {
    if (!externalId.trim()) return;
    setSaving(true);
    try {
      const res = await metaAdsService.saveBudgetMetaMapping({ budgetId, externalCampaignId: externalId.trim() });
      setStatus('valid');
      onSaved?.({ id: (res as any)?.id as string, external_campaign_id: externalId.trim(), budget_id: budgetId });
    } catch (e) {
      console.error(e);
      setStatus('invalid');
      alert('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await metaAdsService.unlinkBudgetMetaMapping(budgetId);
      onSaved?.(null);
      setExternalId('');
    } catch (e) {
      console.error(e);
      alert('Failed to unlink mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
          <Link2 className="h-4 w-4 mr-2 text-blue-500" />
          Map to Meta Campaign
        </div>
        {existing?.external_campaign_id && (
          <div className="text-xs text-gray-500 dark:text-gray-400">Currently: {existing.external_campaign_id}</div>
        )}
      </div>
      <div className="flex items-center space-x-2">
        <input
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          placeholder="Meta Campaign ID (e.g., 1234567890)"
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <button
          onClick={save}
          disabled={saving || !externalId.trim()}
          className="inline-flex items-center px-3 py-2 text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save
        </button>
        <button
          onClick={clear}
          disabled={saving}
          className="inline-flex items-center px-3 py-2 text-sm rounded-md text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <Unlink className="h-4 w-4 mr-2" />
          Unlink
        </button>
      </div>
      {status === 'invalid' && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">Failed to save mapping—check the campaign ID.</div>
      )}
      {status === 'valid' && (
        <div className="mt-2 text-xs text-green-600 dark:text-green-400">Mapping saved.</div>
      )}
    </div>
  );
};
